import React from "react";
import axios from "axios";

import { apiClient } from "../../api/apiClient";
import { useQuery } from "@tanstack/react-query";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";
import {
  type FindingQueryResult,
  type Finding,
  FindingTableUpdateArg,
} from "../../types/findings";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

export function useCreateFinding({ table }: FindingTableUpdateArg) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (finding: Finding) => {
      try {
        const response = await apiClient.post("/api/findings/", finding);

        table.setCreatingRow(null);
        toast.success(`Finding ${finding.name} created successfully`);
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const errorData = error.response.data;
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              const errorMessage = errorData[key].join(", ");
              toast.error(`${key.toUpperCase()}: ${errorMessage}`);
            } else {
              toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
            }
          }
        } else {
          toast.error(`Error creating finding: ${error}`);
        }
      }
    },
    onSuccess: (newFinding: Finding) => {
      queryClient.setQueryData<Finding[]>(["findings"], (prevFindings = []) => [
        ...prevFindings,
        newFinding,
      ]);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["findings"] }),
  });
}

export function useDeleteFinding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (findingId: string) => {
      try {
        await apiClient.delete(`/api/findings/${findingId}`);
        toast.success("Finding successfully deleted");
      } catch (error) {
        toast.error("Failed to delete the finding");
      }
    },
    onMutate: (findingId: string) => {
      queryClient.setQueryData(["findings"], (prevFindings: any) =>
        prevFindings?.filter((finding: Finding) => finding.id !== findingId)
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["findings"] }),
  });
}

export const useExportFindings = (columnFilters: any, sorting: any) => {
  const exportFindings = async () => {
    try {
      const response = await apiClient.post("/api/findings/export/", {
        params: {
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;

      const now = new Date();
      const dateString = now.toISOString().split("T")[0];
      const timestamp = now.getTime();
      const filename = `Findings-${dateString}-${timestamp}.csv`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();

      toast.success(`${filename} downloaded successfully`);
    } catch (error) {
      toast.error(`Failed to download file : ${error}`);
    }
  };

  return exportFindings;
};

export function useSearchFindings({ q }: { q: string }) {
  return useQuery<FindingQueryResult[]>({
    queryKey: ["findings", q],
    queryFn: async () => {
      try {
        const url = `/api/findings?search=${q}`;

        const response = await apiClient.get(url);

        return [
          {
            findings: response.data.results as Finding[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ findings: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useGetAllFindings() {
  return useQuery<FindingQueryResult[]>({
    queryKey: ["all_findings"],
    queryFn: async () => {
      try {
        const url = `/api/findings`;

        const response = await apiClient.get(url);
        return [
          {
            findings: response.data.results as Finding[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ findings: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: false,
  });
}

export function useGetFindings({
  pagination,
  columnFilters,
  sorting,
  globalFilter,
  exportCsv,
}: {
  pagination: MRT_PaginationState;
  columnFilters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
  globalFilter: string | undefined;
  exportCsv?: boolean;
}) {
  return useQuery<FindingQueryResult[]>({
    queryKey: [
      "findings",
      pagination,
      columnFilters,
      sorting,
      globalFilter,
      exportCsv,
    ],
    queryFn: async () => {
      try {
        const url = `/api/findings`;
        const params = new URLSearchParams({
          page: (pagination.pageIndex + 1).toString(),
          limit: pagination.pageSize.toString(),
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
          globalFilter: globalFilter ?? "",
        });

        const response = await apiClient.get(url, { params });

        return [
          {
            findings: response.data.results as Finding[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ findings: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useGetFindingDetail({ event_id }: { event_id: string }) {
  return useQuery<Finding[]>({
    queryKey: ["findings"],
    queryFn: async () => {
      try {
        const url = `/api/findings`;
        const params = new URLSearchParams({
          event_id: event_id,
        });

        const response = await apiClient.get(url, { params });

        return response.data.results;
      } catch (error) {
        //
        return {};
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useUpdateFinding({ table }: FindingTableUpdateArg) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (finding: Finding) => {
      try {
        await apiClient.patch(`/api/findings/${finding.id}/`, finding);

        table.setEditingRow(null);
        toast.success(`Finding ${finding.name} updated successfully`);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const errorData = error.response.data;
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              const errorMessage = errorData[key].join(", ");
              toast.error(`${key.toUpperCase()}: ${errorMessage}`);
            } else {
              toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
            }
          }
        } else {
          toast.error(`Error updating finding ${error}`);
        }
      }
    },

    onMutate: (newFindingInfo: Finding) => {
      queryClient.setQueryData(["findings"], (prevFindings: any) =>
        prevFindings?.map((prevFinding: Finding) =>
          prevFinding.id === newFindingInfo.id ? newFindingInfo : prevFinding
        )
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["findings"] }),
  });
}

export const usePollFindings = () => {
  const pollFindings = async () => {
    try {
      await apiClient.get("/api/findings/poll/", {
        params: {},
      });

      toast.success(`Polled successfully`);
    } catch (error) {
      toast.error("Failed to poll");
    } finally {
      /* setTimeout(() => {
                window.location.reload();
            }, 1000); */
    }
  };

  return pollFindings();
};

export const useDeleteFindings = (
  columnFilters: any,
  sorting: any,
  rowCount: number
) => {
  const deleteFindings = async () => {
    try {
      await apiClient.post("/api/findings/delete/", {
        params: {
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
        },
      });

      toast.success(`Deleted ${rowCount} rows successfully`);
    } catch (error) {
      toast.error("Failed to delete rows");
    } finally {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  return deleteFindings;
};
